import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bot, Sparkles, ShieldAlert, Send, ClipboardCopy } from "lucide-react";

// Hidden, admin-only console for previewing the exact composed Oracle AI CFO
// system prompt customers hit — before any risky prompt change goes to prod.
// Auth: server-side `_require_internal_admin` gates the /api/admin/oracle-qa/preview
// endpoint against the ADMIN_EMAILS env allowlist.  Non-admin requests return 403.
export default function AdminOracleQA() {
  const { user } = useAuth();
  const [message, setMessage] = useState("What are the three plans and pricing?");
  const [mockStateRaw, setMockStateRaw] = useState(
    `financial_health_score=87
audit_risk_level=Low
deduction_efficiency_pct=94%
unassigned_receipts_count=3
total_receipts_count=214
flagged_loophole_hint=Section 179 · unclaimed home-office deduction
total_monthly_card_spend=$4,215
projected_runway_days=182
cpa_readiness_pct=91%`
  );
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [promptDump, setPromptDump] = useState("");
  const [promptCharCount, setPromptCharCount] = useState(0);
  const [forbidden, setForbidden] = useState(false);

  const parseMockState = (raw) => {
    const out = {};
    (raw || "").split(/\r?\n/).forEach((line) => {
      const [k, ...rest] = line.split("=");
      if (!k) return;
      const key = k.trim();
      const val = rest.join("=").trim();
      if (key && val) out[key] = val;
    });
    return out;
  };

  const runPreview = async () => {
    if (!message.trim() || loading) return;
    setLoading(true);
    setForbidden(false);
    setReply("");
    setPromptDump("");
    setPromptCharCount(0);
    try {
      const { data } = await api.post("/admin/oracle-qa/preview", {
        message,
        mock_dashboard_state: parseMockState(mockStateRaw),
      });
      setReply(data.reply || "");
      setPromptDump(data.composed_system_prompt || "");
      setPromptCharCount(data.prompt_char_count || 0);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Preview failed";
      if (status === 403) {
        setForbidden(true);
        toast.error("Admin allowlist required — add your email to ADMIN_EMAILS");
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const copyPrompt = async () => {
    if (!promptDump) return;
    try {
      await navigator.clipboard.writeText(promptDump);
      toast.success("Composed system prompt copied");
    } catch {
      toast.error("Clipboard blocked");
    }
  };

  return (
    <DashboardLayout>
      <div className="fade-up max-w-5xl" data-testid="oracle-qa-page">
        <div className="mb-6">
          <div className="text-cyan-400 font-mono text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2">
            <ShieldAlert size={12} /> Internal Admin · Oracle QA Console
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter text-white flex items-center gap-3">
            <Bot size={28} className="text-cyan-300" /> Oracle AI CFO · Prompt Preview
          </h1>
          <p className="text-slate-400 mt-2 text-sm max-w-2xl">
            Rehearse any user message against the exact composed Oracle system prompt customers hit. Payloads here are
            <span className="text-cyan-300"> never persisted</span> to chat sessions. Use this to verify prompt drift
            before shipping to production.
          </p>
        </div>

        {forbidden && (
          <div
            data-testid="oracle-qa-forbidden"
            className="mb-6 rounded-lg p-4 border border-red-500/40 bg-red-500/5 text-red-200 text-sm"
          >
            <div className="font-semibold text-red-100">Admin allowlist required</div>
            <div className="mt-1 text-red-200/80">
              Signed in as <span className="font-mono">{user?.email}</span> — add this email to the backend
              <span className="font-mono"> ADMIN_EMAILS </span> env var and restart backend to unlock this console.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* LEFT · inputs */}
          <div className="subpage-glass-canvas p-6 space-y-5" data-testid="oracle-qa-inputs">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.24em] text-cyan-300 mb-2 flex items-center gap-1.5">
                <Sparkles size={11} /> Probe message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                data-testid="oracle-qa-message-input"
                className="w-full rounded-lg bg-slate-900/70 border border-cyan-500/25 focus:border-cyan-400 outline-none text-white text-sm p-3 font-mono"
                placeholder="Ask what the customer would ask…"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.24em] text-cyan-300 mb-2 flex items-center gap-1.5">
                Mock dashboard state (KEY=VALUE per line)
              </label>
              <textarea
                value={mockStateRaw}
                onChange={(e) => setMockStateRaw(e.target.value)}
                rows={10}
                data-testid="oracle-qa-mockstate-input"
                className="w-full rounded-lg bg-slate-900/70 border border-cyan-500/25 focus:border-cyan-400 outline-none text-slate-200 text-xs p-3 font-mono"
              />
              <div className="text-[10px] text-slate-500 mt-1 font-mono">
                Every non-empty line becomes a read-only token in <span className="text-cyan-300">current_user_dashboard_state</span>.
              </div>
            </div>
            <button
              onClick={runPreview}
              disabled={loading}
              data-testid="oracle-qa-run-btn"
              className="btn-neon h-11 px-5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold flex items-center gap-2 disabled:opacity-60"
            >
              {loading ? "Consulting Oracle…" : (<><Send size={14} /> Preview Oracle reply</>)}
            </button>
          </div>

          {/* RIGHT · outputs */}
          <div className="space-y-5">
            <div className="subpage-glass-canvas p-6" data-testid="oracle-qa-reply-card">
              <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-emerald-300 flex items-center gap-1.5 mb-2">
                <Bot size={11} /> Oracle reply
              </div>
              <div
                data-testid="oracle-qa-reply-body"
                className="text-slate-100 text-sm whitespace-pre-wrap min-h-[120px] font-sans leading-relaxed"
              >
                {reply || <span className="text-slate-500 italic">Run a preview to see the composed reply here.</span>}
              </div>
            </div>

            <div className="subpage-glass-canvas p-6" data-testid="oracle-qa-prompt-card">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-cyan-300">
                  Composed system prompt · {promptCharCount.toLocaleString()} chars
                </div>
                <button
                  onClick={copyPrompt}
                  disabled={!promptDump}
                  data-testid="oracle-qa-copy-prompt-btn"
                  className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 hover:text-cyan-100 disabled:opacity-40 flex items-center gap-1"
                >
                  <ClipboardCopy size={11} /> Copy
                </button>
              </div>
              <pre
                data-testid="oracle-qa-prompt-body"
                className="text-slate-300 text-[11px] font-mono whitespace-pre-wrap max-h-[420px] overflow-y-auto rounded bg-slate-950/60 p-3 border border-cyan-500/10"
              >
                {promptDump || <span className="text-slate-600 italic">The full anchor + Master Bot Context Matrix + mock dashboard tokens will appear here.</span>}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
